import { TokenManager } from "@/utils/api-client-factory";

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  "https://main-api-production-1bb6.up.railway.app/api";

export interface PriceAlertConfigData {
  id: string;
  userId: string;
  exchange: string;
  tradingPair: string | null;
  alertType: "SPIKE" | "DROP";
  changeThreshold: number;
  timeframe: string;
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
  updatedAt: string;
}

export interface PriceAlertEventData {
  id: string;
  symbol: string;
  exchange: string;
  alertType: string;
  startPrice: number;
  currentPrice: number;
  changePercent: number;
  interval: string;
  timestamp: string;
  createdAt: string;
  /** Recent 15 m closes (oldest first), built once on the api for every user. */
  sparkline?: number[];
}

export interface PriceAlertEventsPage {
  events: PriceAlertEventData[];
  page: number;
  hasMore: boolean;
}

export interface CreatePriceAlertConfigPayload {
  exchange: string;
  tradingPair?: string;
  alertType: "SPIKE" | "DROP";
  changeThreshold: number;
  timeframe: string;
}

export interface UpdatePriceAlertConfigPayload {
  exchange?: string;
  tradingPair?: string | null;
  alertType?: "SPIKE" | "DROP";
  changeThreshold?: number;
  timeframe?: string;
  status?: "ACTIVE" | "DISABLED";
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = TokenManager.getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Request failed with status ${res.status}`);
  }

  const json = await res.json();
  return json.data ?? json;
}

export interface PriceAlertStatsData {
  activeAlerts: number;
  triggered7d: number;
  triggeredToday: number;
  exchangesWatched: number;
  paused: number;
}

export const getPriceAlertStats = (userId: string): Promise<PriceAlertStatsData> =>
  apiFetch(`/price-alerts/stats/${userId}`);

export const getPriceAlertConfigs = (userId: string): Promise<PriceAlertConfigData[]> =>
  apiFetch(`/price-alerts/configs/${userId}`);

export const createPriceAlertConfig = (
  userId: string,
  payload: CreatePriceAlertConfigPayload,
): Promise<PriceAlertConfigData> =>
  apiFetch(`/price-alerts/configs/${userId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updatePriceAlertConfig = (
  userId: string,
  configId: string,
  payload: UpdatePriceAlertConfigPayload,
): Promise<PriceAlertConfigData> =>
  apiFetch(`/price-alerts/configs/${userId}/${configId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

export const deletePriceAlertConfig = (
  userId: string,
  configId: string,
): Promise<void> =>
  apiFetch(`/price-alerts/configs/${userId}/${configId}`, {
    method: "DELETE",
  });

export const getPriceAlertEventsBySymbol = (
  symbol: string,
  limit = 20,
): Promise<PriceAlertEventData[]> =>
  apiFetch(`/price-alerts/history/${encodeURIComponent(symbol)}?limit=${limit}`);

export const getPriceAlertEvents = (
  userId: string,
  params?: { limit?: number; page?: number },
): Promise<PriceAlertEventsPage> => {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.page) searchParams.set("page", params.page.toString());
  const qs = searchParams.toString();
  return apiFetch(`/price-alerts/events/${userId}${qs ? `?${qs}` : ""}`);
};
